import createPostingPreview from '../../views/nominal/NominalPostingPreviewForm';
import createPostedTransactions from '../../views/nominal/PostedTransactionsForm';
import SalesCreditPostingPreview from '../../graphql/entities/salesLedger/SalesCreditPostingPreview';
import SalesCreditTransactions from '../../graphql/entities/salesLedger/SalesCreditTransactions';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import { IDeprecatedFormAction, IFormAction } from '../../@types/entityTypes';
import { DocumentStatus, ISalesCredit, SalesCreditType } from '../../graphql/schema/p4types';
import SalesReturnNoteDocument from '../../graphql/entities/salesLedger/SalesReturnNoteDocument';
import React, { ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import ModalContext from '../../components/Context/ModalContext';
import SalesCredit from '../../graphql/entities/salesLedger/SalesCredit';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import SalesCreditPostModal from '../../components/modals/SalesCreditPostModal';
import { PostSelectButton } from '../../components/SelectButton';
import { isNotArchived } from '../../components/connectedForm/HeaderBlock/HeaderBlock';
import { SalesCreditActions } from './SalesCreditActions';

const LazyProductsStockAdjustmentForm = React.lazy(() => import('../../views/product/ProductsStockAdjustmentForm'));

export const formActionsForPermissions = [
  { actionReference: SalesCreditActions.PreviewPostings },
  { actionReference: SalesCreditActions.ViewTransactions },
  { actionReference: SalesCreditActions.PrintCredit },
  { actionReference: SalesCreditActions.PrintSalesReturnNote },
  { actionReference: SalesCreditActions.ViewStockAdjustment },
  { actionReference: SalesCreditActions.PostCredit },
];

const usePrintCreditAction: () => [IFormAction<SalesCreditActions>, JSX.Element] = () => {
  const connectedForm = useContext(ConnectedFormContext);

  const [performSave, confirmationModals] = usePerformSave();

  const [printCreditModalState, setPrintCreditModalState] = useState<{
    modalOpen: boolean;
  }>({ modalOpen: false });

  const onClickPrint = useCallback(() => {
    if (connectedForm.dirty) {
      performSave({
        onSaveComplete: () => setPrintCreditModalState({ modalOpen: true }),
        submitMeta: { suppressAfterSubmit: true },
      });
    } else {
      setPrintCreditModalState({ modalOpen: true });
    }
  }, [connectedForm.dirty, performSave]);

  return useMemo(
    () => [
      {
        key: 'printCredit',
        text: 'Print Credit',
        condition: (credit: ISalesCredit) => credit && credit.id && credit.documentStatus !== DocumentStatus.Draft,
        actionReference: SalesCreditActions.PrintCredit,
        onClick: onClickPrint,
      },
      <>
        {confirmationModals}
        <ModalContext.Provider
          value={{
            onClose: () => {
              setPrintCreditModalState({ modalOpen: false });
            },
          }}
        >
          <DocumentModal2
            modalOpen={printCreditModalState.modalOpen}
            entity={SalesCredit}
            useCustomerDefaults={true}
            title="Print Credit"
            values={connectedForm.values}
            modalWrapper={true}
            reprint={false}
            multiValue={false}
            onComplete={() => {
              //Reload form data on completion, prevent conflict modal due to flags being altered on the credit record.
              connectedForm.reloadData?.(true);
            }}
          />
        </ModalContext.Provider>
      </>,
    ],
    [confirmationModals, connectedForm, onClickPrint, printCreditModalState.modalOpen]
  );
};

const usePrintReturnAction: () => [IFormAction<SalesCreditActions>, JSX.Element] = () => {
  const connectedForm = useContext(ConnectedFormContext);

  const [performSave, confirmationModals] = usePerformSave();

  const [printReturnNoteModalState, setPrintReturnNoteModalState] = useState<{
    modalOpen: boolean;
  }>({ modalOpen: false });

  const onClickPrint = useCallback(() => {
    if (connectedForm.dirty) {
      performSave({
        onSaveComplete: () => setPrintReturnNoteModalState({ modalOpen: true }),
        submitMeta: { suppressAfterSubmit: true },
      });
    } else {
      setPrintReturnNoteModalState({ modalOpen: true });
    }
  }, [connectedForm.dirty, performSave]);

  return useMemo(
    () => [
      {
        key: 'printReturnNote',
        text: 'Print Return Note',
        condition: (credit: ISalesCredit) => credit && credit.id && credit.salesCreditType === SalesCreditType.Return,
        actionReference: SalesCreditActions.PrintSalesReturnNote,
        onClick: onClickPrint,
        individualButton: true,
      },
      <>
        {confirmationModals}
        <ModalContext.Provider
          value={{
            onClose: () => {
              setPrintReturnNoteModalState({ modalOpen: false });
            },
          }}
        >
          <DocumentModal2
            modalOpen={printReturnNoteModalState.modalOpen}
            entity={SalesReturnNoteDocument}
            useCustomerDefaults={true}
            title="Print Return Note"
            values={connectedForm.values}
            modalWrapper={true}
            reprint={false}
            multiValue={false}
            onComplete={() => {
              //Reload form data on completion, prevent conflict modal due to flags being altered on the credit record.
              connectedForm.reloadData?.(true);
            }}
          />
        </ModalContext.Provider>
      </>,
    ],
    [confirmationModals, connectedForm, onClickPrint, printReturnNoteModalState.modalOpen]
  );
};

const usePostAction = ({
  viaTradeCounter,
}: {
  viaTradeCounter: boolean;
}): [IFormAction<SalesCreditActions>, ReactElement] => {
  const { readOnly } = useContext(ConnectedFormContext);
  const [isOpen, setIsOpen] = useState(false);
  const [performSave, confirmationModals] = usePerformSave((result) => {
    setIsOpen(true);
  });
  const connectedForm = useContext(ConnectedFormContext);

  const action: IFormAction<SalesCreditActions> = {
    key: 'postCredit',
    actionReference: SalesCreditActions.PostCredit,
    individualButton: true,
    individualButtonComponent: PostSelectButton,
    text: connectedForm.values?.stockAdjustmentUUID ? 'Update Adjustment & Post' : 'Post Credit',
    condition: (credit) => !readOnly && isNotArchived(credit) && isDraftOrReleased(credit),
    onClick: () => {
      performSave({ submitMeta: { suppressAfterSubmit: true } });
    },
  };

  return [
    action,
    <>
      {confirmationModals}
      <SalesCreditPostModal isOpen={isOpen} viaTradeCounter={viaTradeCounter} onClose={() => setIsOpen(false)} />
    </>,
  ];
};

const SALES_CREDIT_POSTING_PREVIEW = createPostingPreview(SalesCreditPostingPreview);
const SALES_CREDIT_POSTED_TRANSACTIONS = createPostedTransactions(SalesCreditTransactions);

export const useSalesCreditActions: (
  viaTradeCounter: boolean
) => [Array<IFormAction<SalesCreditActions> | IDeprecatedFormAction<SalesCreditActions>>, ReactElement] = (
  viaTradeCounter
) => {
  const [printCreditAction, printCreditModals] = usePrintCreditAction();
  const [printReturnAction, printReturnModals] = usePrintReturnAction();
  const [postAction, postModal] = usePostAction({
    viaTradeCounter: viaTradeCounter,
  });
  const { readOnly } = useContext(ConnectedFormContext);

  return useMemo(
    () => [
      [
        {
          actionReference: SalesCreditActions.PreviewPostings,
          description: 'Preview Postings',
          triggerModal: {
            modalContent: SALES_CREDIT_POSTING_PREVIEW,
            entityId: 'id',
            pageView: true,
            modalProps: {
              formEntityCustomUrl: 'nominal/Postings/salesLedger/SalesCredit',
              forceReadOnly: true,
            },
          },
          performSave: true,
          saveMessage: 'Nominal/VAT Postings Preview',
          btnClass: 'btnInfo',
          individualButton: true,
          condition: (credit) => isDraftOrReleased(credit) && !readOnly,
        },
        {
          actionReference: SalesCreditActions.ViewTransactions,
          description: 'View Transactions',
          triggerModal: {
            modalContent: SALES_CREDIT_POSTED_TRANSACTIONS,
            entityId: 'id',
            pageView: true,
            modalProps: {
              formEntityCustomUrl: 'nominal/Transactions/salesLedger/SalesCredit',
              forceReadOnly: true,
            },
          },
          btnClass: 'btnInfo',
          individualButton: true,
          condition: displayPosted,
        },
        printCreditAction,
        printReturnAction,
        {
          actionReference: SalesCreditActions.ViewStockAdjustment,
          description: 'View Stock Adjustment',
          triggerModal: {
            modalContent: LazyProductsStockAdjustmentForm,
            entityId: 'stockAdjustment.id',
            pageView: true,
            modalProps: {
              formEntityCustomUrl: 'products/StockControl/StockAdjustment/Details/',
              forceReadOnly: true,
            },
          },
          btnClass: 'btnInfo',
          individualButton: true,
          condition: (credit: ISalesCredit) => credit?.stockAdjustment?.id,
        },
        postAction,
      ],
      <>
        {printCreditModals}
        {printReturnModals}
        {postModal}
      </>,
    ],
    [postAction, postModal, printCreditAction, printCreditModals, printReturnAction, printReturnModals]
  );
};

function displayPosted(credit: ISalesCredit) {
  if (credit && credit.documentStatus) {
    return credit.documentStatus === 'POSTED';
  }
  return false;
}

export function isDraftOrReleased(credit: ISalesCredit) {
  if (credit && credit.documentStatus) {
    return credit.documentStatus === 'DRAFT' || credit.documentStatus === 'RELEASED';
  }
  return false;
}
